import { useCallback } from 'react';
import useAppSelector from './useAppSelector';
import { clearError } from '../store/slices/authSlice';
import useAppDispatch from './useAppDispatch';
import useAuth0Redirect from './useAuth0Redirect';
import useAuthentikRedirect from './useAuthentikRedirect';
import { useAuth0 } from '@auth0/auth0-react';
import { getStoredAccessToken } from '../utils/auth0Utils';

// Build-time constant — Vite replaces this with the literal string at compile time.
const AUTH_PROVIDER = import.meta.env.VITE_AUTH_PROVIDER || 'auth0';

type ProviderHookResult = {
  isAuthenticated: boolean;
  isLoading: boolean;
  isRedirecting: boolean;
  user: unknown;
  error: Error | null;
  logout: () => void;
  getToken: () => Promise<string>;
  signinRedirect: (returnTo?: string) => void;
  updateProfile?: (userData: unknown) => Promise<unknown>;
};

// Module-level hook selection — AUTH_PROVIDER is a build-time constant so only
// one branch executes. The other is dead code eliminated by the bundler.
// Both branches are hoisted here so useAuth always calls the same hooks on every render.
let useProviderHook: (forceUpdate?: boolean) => ProviderHookResult;

if (AUTH_PROVIDER === 'authentik') {
  useProviderHook = useAuthentikRedirect as unknown as (forceUpdate?: boolean) => ProviderHookResult;
} else {
  // In the Auth0 build, this combined function is always the hook used.
  // It calls two hooks unconditionally — no conditional hook violation.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useProviderHook = (forceUpdate = false): ProviderHookResult => {
    const redirect = useAuth0Redirect(forceUpdate); // eslint-disable-line react-hooks/rules-of-hooks
    const { user, isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0(); // eslint-disable-line react-hooks/rules-of-hooks
    return {
      isAuthenticated,
      isLoading: isLoading || redirect.isRedirecting,
      isRedirecting: redirect.isRedirecting,
      user,
      error: error || null,
      logout: redirect.logout,
      getToken: redirect.getToken,
      signinRedirect: (returnTo?: string) =>
        loginWithRedirect({ appState: { returnTo } }),
      updateProfile: redirect.updateProfile,
    };
  };
}

/**
 * Provider-agnostic auth hook.
 * Internally delegates to useAuthentikRedirect (Authentik build) or
 * useAuth0Redirect + useAuth0 (Auth0 build).
 */
export const useAuth = (forceUpdate = false) => {
  const dispatch = useAppDispatch();
  const hook = useProviderHook(forceUpdate);
  const { error: storeError } = useAppSelector((state) => state.auth);

  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    user: hook.user,
    accessToken: getStoredAccessToken(),
    refreshToken: null,
    isAuthenticated: hook.isAuthenticated,
    isLoading: hook.isLoading,
    isRedirecting: hook.isRedirecting,
    error: hook.error || storeError,
    login: () => { window.location.href = '/'; },
    logout: hook.logout,
    clearError: handleClearError,
    getToken: hook.getToken,
    signinRedirect: hook.signinRedirect,
    updateProfile: hook.updateProfile,
  };
};

export default useAuth;
